import React, { useState, useEffect } from 'react';
import { ProductType, ProductTypesRes, useGetProductTypes } from '../../queries/useGetProductTypes';
import { ProductItem } from '../../types';
import { MakeModelsRes, useGetMakeModels } from '../../queries/useGetMakeModels';
import { capitaliseString } from '@luna-protocol/core/src/utils/constants/format.ts';
import config from '@luna-protocol/core/src/utils/feature-flags.json';
import { FormikHelpers } from 'formik';
import { OEMType as EOEMType, SelectOption } from '@luna-protocol/core/src/types';
import { SerialNumber } from '../../types';
import EditableInput from '../EditableInput/EditableInput';
import EditableSelect from '../EditableSelect/EditableSelect';
import { useIntl } from 'react-intl';
import messages from './ProductFormFields.messages.ts';
import { useAppStore } from '../../useAppStore.ts';

type FormValues = { serialNumbers: SerialNumber[] };
type SetFieldValueType = FormikHelpers<FormValues>['setFieldValue'];
type SummarySectionHandlerProps = {
  item: ProductItem;
  editable: boolean;
  isEditable: boolean;
  unitPrefix: string;
  fieldIdx: number;
  serial: SerialNumber;
  setFieldValue: SetFieldValueType;
  index: number;
  handleAssetClassChange: (value: string, setFieldValue: SetFieldValueType, index: number) => void;
  handleModelChange: (value: string, setFieldValue: SetFieldValueType, index: number) => void;
  handleMakeChange: (value: string, setFieldValue: SetFieldValueType, index: number) => void;
  handleChange: React.ChangeEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
  getInvoiceTitle: () => string;
};

const ProductFormFields: React.FC<SummarySectionHandlerProps> = ({
  item,
  editable,
  isEditable,
  unitPrefix,
  fieldIdx,
  serial,
  setFieldValue,
  index,
  handleAssetClassChange,
  handleMakeChange,
  handleModelChange,
  // handleChange, may be used by standard input components, not EditableInput
  handleBlur,
  getInvoiceTitle,
}) => {
  const { formatMessage } = useIntl();
  const OEMType = useAppStore(state => state.OEMType);
  const dealerFlowTemplate = useAppStore(state => state.dealerFlowTemplate);
  const mainAssetClass = serial.serial_number_asset_class;
  const { data: makeModel } = useGetMakeModels(mainAssetClass);
  const { data: productTypes } = useGetProductTypes();
  const [rawInvoiceValue, setRawInvoiceValue] = useState('');

  useEffect(() => {
    if (isEditable) {
      const currentValue = serial.nada_msrp_invoice;
      if (currentValue !== undefined && currentValue !== null && currentValue > 0) {
        setRawInvoiceValue(String(currentValue));
      } else {
        setRawInvoiceValue('');
      }
    }
  }, [isEditable, serial.nada_msrp_invoice]);

  const getFilterType = () => {
    let filterType = '';
    switch (OEMType) {
      case EOEMType.Brunswick:
        filterType = 'brunswick';
        break;
      case EOEMType.KTM:
        filterType = 'ktm';
        break;
      case EOEMType.EZGO:
        filterType = 'ezgo';
        break;
      case EOEMType.CUSTOM:
        filterType = 'custom';
        break;
      default:
        filterType = '';
        break;
    }
    if (config.FF_OEMTemplatesEnabled && dealerFlowTemplate) {
      filterType = dealerFlowTemplate.name;
    }
    return filterType;
  };

  function makeAssetClassOptions(data: ProductTypesRes | undefined) {
    const opts: SelectOption[] = [];
    if (config.FF_OEMTemplatesEnabled && dealerFlowTemplate && !data) {
      if (dealerFlowTemplate.base_type) {
        return [
          {
            value: dealerFlowTemplate.base_type,
            label: capitaliseString(dealerFlowTemplate.base_type),
          },
        ];
      }
      if (OEMType === 'ezgo') {
        return [
          {
            value: 'golf-cart',
            label: 'Golf Cart',
          },
        ];
      }
      return [];
    }

    if (!data) {
      if (OEMType === 'ktm') {
        return [
          {
            value: 'motorcycle',
            label: 'Motorcycle',
          },
        ];
      }
      if (OEMType === 'ezgo') {
        return [
          {
            value: 'golf-cart',
            label: 'Golf Cart',
          },
        ];
      }
      return [];
    }

    data.product_type.forEach((productType: ProductType) => {
      opts.push({
        value: productType.productType,
        label: productType.productType,
      });
    });

    const uniqueOpts = [...new Map(opts.map(assetClass => [JSON.stringify(assetClass), assetClass])).values()];
    return uniqueOpts;
  }

  function makeYearOptions(
    data: MakeModelsRes | undefined,
    filterMake: string | undefined,
    filterModel: string | undefined,
  ): SelectOption[] {
    if (!data) return [];
    const lowerMake = (filterMake ?? '').trim().toLowerCase();
    const lowerModel = (filterModel ?? '').trim().toLowerCase();
    if (!lowerMake || !lowerModel) return [];
    const filterMatches = data.make_models.filter(
      item => item.make.toLowerCase() === lowerMake && item.model.toLowerCase() === lowerModel,
    );
    const uniqueYears = Array.from(new Set(filterMatches.map(m => m.year).filter(Boolean)));
    return uniqueYears.map(year => ({
      value: year.toString(),
      label: year.toString(),
    }));
  }

  const returnMakeDropdown = (values: SerialNumber, setFieldValue: SetFieldValueType, index: number) => {
    const filterType = getFilterType();
    return (
      <EditableSelect
        editable={isEditable}
        viewOnly={!editable}
        label={formatMessage(messages.productInformation.fields.make)}
        placeholder={formatMessage(messages.productInformation.fields.make)}
        name={`serialNumbers[${index}].make`}
        onChange={option => {
          let makeValue = '';
          if (!option) {
            handleMakeChange('', setFieldValue, index);
            return;
          }
          if (typeof option === 'string') {
            makeValue = option;
            handleMakeChange(option, setFieldValue, index);
          } else if (option && typeof option === 'object' && 'value' in option) {
            makeValue = option.value;
            handleMakeChange(option?.value || '', setFieldValue, index);
          } else {
            makeValue = '';
            handleMakeChange('', setFieldValue, index);
          }
          if (OEMType === 'ezgo' && values.condition === 'new' && makeValue) {
            const match = makeModel?.make_models.find(
              m =>
                m.make.toLowerCase() === makeValue.toLowerCase() &&
                m.model.toLowerCase() === values.model.toLowerCase() &&
                m.year === values.year,
            );
            if (match !== undefined) {
              setFieldValue(`serialNumbers[${index}].nada_msrp_invoice`, `${match.msrp}`);
            }
          }
        }}
        value={values.make}
        options={makeModel?.make_models
          .filter(m => m.oemName.toLowerCase().includes(filterType.toLowerCase()))
          .filter((v, i, a) => a.findIndex(t => t.make === v.make) === i)
          .map(m => ({
            value: m.make,
            label: m.make,
          }))}
        isClearable
        allowCreate
      />
    );
  };

  const returnModelDropdown = (values: SerialNumber, setFieldValue: SetFieldValueType, index: number) => {
    const filterType = getFilterType();
    return (
      <EditableSelect
        editable={isEditable}
        viewOnly={!editable}
        label={formatMessage(messages.productInformation.fields.model)}
        placeholder={formatMessage(messages.productInformation.fields.model)}
        name={`serialNumbers[${index}].model`}
        onChange={option => {
          let modelValue = '';
          if (typeof option === 'string') {
            modelValue = option;
            handleModelChange(option, setFieldValue, index);
          } else if (option && typeof option === 'object' && 'value' in option) {
            modelValue = option.value;
            handleModelChange(option?.value || '', setFieldValue, index);
          } else {
            modelValue = '';
            handleModelChange('', setFieldValue, index);
          }
          if (OEMType === 'ezgo' && values.condition === 'new' && modelValue) {
            const match = makeModel?.make_models.find(
              m =>
                m.make.toLowerCase() === values.make.toLowerCase() &&
                m.model.toLowerCase() === modelValue.toLowerCase() &&
                m.year === values.year,
            );
            if (match !== undefined) {
              setFieldValue(`serialNumbers[${index}].nada_msrp_invoice`, `${match.msrp}`);
            }
          }
        }}
        value={values.model}
        options={makeModel?.make_models
          .filter(m => m.oemName.toLowerCase().includes(filterType.toLowerCase()))
          .filter(m => m.make.toLowerCase() === values.make.toLowerCase())
          .map(m => ({
            value: m.model,
            label: m.model,
          }))}
        allowCreate
        isClearable
      />
    );
  };

  const formatInvoiceDisplay = (price: string | number | undefined): string => {
    if (price === undefined || price === null || price === '' || price === 0) return '';
    const numValue = typeof price === 'string' ? parseFloat(price) : price;
    return isNaN(numValue) ? '' : numValue.toFixed(2);
  };

  const handleInvoiceChange = (displayPrice: string | number | undefined) => {
    const stringPrice = String(displayPrice || '');
    if (isEditable) {
      setRawInvoiceValue(stringPrice);
    }
    if (stringPrice === '' || stringPrice === '0') {
      setFieldValue(`serialNumbers[${index}].nada_msrp_invoice`, undefined);
      return;
    }
    const numberPrice = parseFloat(stringPrice);
    if (!isNaN(numberPrice) && numberPrice > 0) {
      setFieldValue(`serialNumbers[${index}].nada_msrp_invoice`, numberPrice);
    } else {
      setFieldValue(`serialNumbers[${index}].nada_msrp_invoice`, undefined);
    }
  };

  const handleInvoiceBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const price = e.target.value;
    if (price && price.trim() !== '' && price.trim() !== '0') {
      const numberPrice = parseFloat(price);
      if (!isNaN(numberPrice) && numberPrice > 0) {
        setFieldValue(`serialNumbers[${index}].nada_msrp_invoice`, numberPrice);
      } else {
        setFieldValue(`serialNumbers[${index}].nada_msrp_invoice`, undefined);
      }
    } else {
      setFieldValue(`serialNumbers[${index}].nada_msrp_invoice`, undefined);
    }

    handleBlur(e);
  };

  switch (item.title) {
    case 'Item':
      return (
        <div className={`select--input--container--editable`} key={item.title + fieldIdx}>
          <label htmlFor={item.label} className="label">
            {item.title}
          </label>
          <p className={editable ? 'input-value editable' : 'input-value'}>{item.value}</p>
        </div>
      );
    case `${unitPrefix} Make`:
      return returnMakeDropdown(serial, setFieldValue, index);
    case `${unitPrefix} Model`:
      return returnModelDropdown(serial, setFieldValue, index);
    case `${unitPrefix} Asset Class`:
      return (
        <EditableSelect
          key={fieldIdx}
          editable={isEditable}
          viewOnly={!editable}
          label={formatMessage(messages.productInformation.fields.asset_class)}
          placeholder={formatMessage(messages.productInformation.fields.asset_class)}
          name={`serialNumbers[${index}].serial_number_asset_class`}
          onChange={option => {
            if (option && typeof option === 'object' && 'value' in option) {
              handleAssetClassChange(option?.value || '', setFieldValue, index);
            } else if (typeof option === 'string') {
              handleAssetClassChange(option, setFieldValue, index);
            } else {
              handleAssetClassChange('', setFieldValue, index);
            }
          }}
          value={serial.serial_number_asset_class}
          options={makeAssetClassOptions(productTypes)}
          isClearable
        />
      );
    case `${unitPrefix} Color`:
      return (
        <EditableInput
          key={fieldIdx}
          label={formatMessage(messages.productInformation.fields.color)}
          name={`serialNumbers[${index}].color`}
          value={serial.color}
          onChange={newValue => setFieldValue(`serialNumbers[${index}].color`, newValue)}
          editable={isEditable}
          viewOnly={!editable}
          formik={true}
          onBlur={handleBlur}
        />
      );
    case `${unitPrefix} Year`:
      return (
        <EditableSelect
          key={fieldIdx}
          editable={isEditable}
          viewOnly={!editable}
          label={formatMessage(messages.productInformation.fields.year)}
          placeholder={formatMessage(messages.productInformation.fields.year)}
          name={`serialNumbers[${index}].year`}
          onChange={option => {
            if (option && typeof option === 'object' && 'value' in option) {
              setFieldValue(`serialNumbers[${index}].year`, option.value);
            } else if (typeof option === 'string') {
              setFieldValue(`serialNumbers[${index}].year`, option);
            } else {
              setFieldValue(`serialNumbers[${index}].year`, '');
            }
          }}
          value={serial.year?.toString()}
          options={makeYearOptions(makeModel, serial.make, serial.model)}
          allowCreate
        />
      );
    case `${unitPrefix} Number`:
      return (
        <EditableInput
          key={fieldIdx}
          label={formatMessage(messages.productInformation.fields.serial_number)}
          name={`serialNumbers[${index}].serial_number`}
          value={serial.serial_number}
          onChange={newValue => setFieldValue(`serialNumbers[${index}].serial_number`, newValue)}
          editable={isEditable}
          viewOnly={!editable}
          formik={true}
          onBlur={handleBlur}
        />
      );
    case `${unitPrefix} Price`:
      return (
        <EditableInput
          key={fieldIdx}
          type="number"
          label={formatMessage(messages.productInformation.fields.price)}
          name={`serialNumbers[${index}].product_price`}
          value={serial.product_price}
          onChange={newValue => setFieldValue(`serialNumbers[${index}].product_price`, newValue)}
          editable={isEditable}
          viewOnly={!editable}
          formik={true}
          onBlur={handleBlur}
        />
      );
    case `${unitPrefix} Condition`:
      return (
        <EditableSelect
          key={fieldIdx}
          editable={isEditable}
          viewOnly={!editable}
          label={formatMessage(messages.productInformation.fields.condition)}
          placeholder={formatMessage(messages.productInformation.fields.condition)}
          name={`serialNumbers[${index}].condition`}
          onChange={option => {
            if (option && typeof option === 'object' && 'value' in option) {
              setFieldValue(`serialNumbers[${index}].condition`, option.value);
            } else if (typeof option === 'string') {
              setFieldValue(`serialNumbers[${index}].condition`, option);
            } else {
              setFieldValue(`serialNumbers[${index}].condition`, '');
            }
          }}
          value={serial.condition}
          options={[
            { value: 'New', label: 'New' },
            { value: 'Used', label: 'Used' },
          ]}
        />
      );
    case `${unitPrefix} NADA/MSRP Invoice`:
      return (
        <EditableInput
          key={fieldIdx}
          type="text"
          label={getInvoiceTitle()}
          name={`serialNumbers[${index}].nada_msrp_invoice`}
          value={isEditable ? rawInvoiceValue : formatInvoiceDisplay(serial.nada_msrp_invoice)}
          onChange={handleInvoiceChange}
          editable={isEditable}
          viewOnly={!editable}
          formik={true}
          onBlur={handleInvoiceBlur}
        />
      );
    default:
      return null;
  }
};

export default ProductFormFields;
